import { useEffect, useContext } from "react";
import { useAuth, AuthContext } from "./useAuth";
import { useHeartbeat } from "./useHeartbeat";
import { useActivityDetection } from "./useActivityDetection";
import { useMultiTabSync } from "./useMultiTabSync";
import { updateOnlineStatus } from "@/utils/onlineStatusUtils";

interface UseOnlineStatusOptions {
  enabled?: boolean;
  inactivityTimeout?: number;
}

export const useOnlineStatus = ({
  enabled = true,
  inactivityTimeout = 30 * 60 * 1000,
}: UseOnlineStatusOptions = {}) => {
  // Check if AuthContext is available before using useAuth
  const authContext = useContext(AuthContext);
  const { user } = authContext ? useAuth() : { user: null };
  const userId = user?.id || "";
  const userRole = user?.role || "";

  // Multi-tab synchronization
  const { isMasterTab } = useMultiTabSync({
    userId,
    isEnabled: enabled && userRole === "tutor",
    onBecomeMaster: () => {
      console.log("This tab became the master tab for online status");
    },
    onLoseMaster: () => {
      console.log("This tab lost master status");
    },
  });

  // Heartbeat (only in master tab)
  useHeartbeat({
    userId,
    userRole,
    isEnabled: enabled && isMasterTab && userRole === "tutor",
    onError: (error) => {
      console.error("Heartbeat failed:", error);
    },
  });

  // Activity detection
  useActivityDetection({
    userId,
    userRole,
    isEnabled: enabled && userRole === "tutor",
    inactivityTimeout,
    onInactive: () => {
      console.log("User has been inactive, marking as offline");
    },
  });

  // Page visibility handling
  useEffect(() => {
    if (!enabled || !userId || userRole !== "tutor") return;

    const handleVisibilityChange = async () => {
      if (document.hidden) {
        console.log("Tab hidden, reducing heartbeat frequency");
      } else {
        console.log("Tab visible, resuming normal heartbeat");
        // Send immediate heartbeat when tab becomes visible
        if (isMasterTab) {
          await updateOnlineStatus(userId, userRole, true);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [userId, userRole, enabled, isMasterTab]);

  // Beforeunload and unload handling
  useEffect(() => {
    if (!enabled || !userId || userRole !== "tutor") return;

    const handleBeforeUnload = async () => {
      // Use sendBeacon for reliable sending on page unload
      try {
        const data = new Blob(
          [
            JSON.stringify({
              userId,
              userRole,
              isOnline: false,
              timestamp: new Date().toISOString(),
            }),
          ],
          { type: "application/json" },
        );

        // Note: In a real implementation, you'd send this to an endpoint
        // that handles the offline status update
        console.log("Tab closing, attempting to mark offline");

        // Synchronous attempt to mark offline
        updateOnlineStatus(userId, userRole, false);
      } catch (error) {
        console.error("Failed to mark offline on unload:", error);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [userId, userRole, enabled]);

  return {
    isMasterTab,
    isEnabled: enabled && userRole === "tutor",
  };
};
